/**
 * App data TABLES manager (ADR-0048 single-IDE) — a builder rail BOTTOM-zone area
 * (the app-global zone, next to App Query / Stato globale / Librerie), where the
 * operator AUTHORS the app's data tables WITHOUT leaving the editor. This is the
 * first panel migrated off the standalone app-detail page toward the north-star
 * "app-detail goes away; everything lives in the editor".
 *
 * It follows the WS-7/WS-8 rail-bottom MASTER/DETAIL pattern (mirror of
 * `AppQueriesPane`/`CustomComponentsPane`): a `view` prop (`"list"|"detail"|"full"`)
 * with CONTROLLED selection lifted to `PageBuilderPage`, so it mounts TWICE — the
 * LIST in the lateral sidebar, the DETAIL in the content. Create is IMMEDIATE
 * server-side (no unsaved draft): "＋ Nuova tabella" creates a table + selects it.
 * The DETAIL reuses the SHARED `TableDesigner` (grid + Advanced drawer + dialogs) —
 * the same component the standalone `TableDesignerPage` route renders, so there's
 * ONE designer. The `["apps", appId, "tables"]` TanStack query is cache-shared with
 * the builder's table pickers, so a create/delete here repopulates them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Table2, Trash2 } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/lib/api";
import { appsApi, type TableSummary } from "@/lib/api-apps";

import { TableDesigner } from "../TableDesigner";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** Next free `tabella_N` so an immediate create never collides (409). */
function nextTableName(tables: TableSummary[]): string {
  const names = new Set(tables.map((t) => t.name));
  for (let n = 1; ; n += 1) {
    const candidate = `tabella_${n}`;
    if (!names.has(candidate)) return candidate;
  }
}

type AreaView = "list" | "detail" | "full";

export function AppTablesPane({
  appId,
  canWrite = true,
  view = "full",
  selectedId: selectedIdProp,
  onSelectId,
}: {
  appId: string;
  canWrite?: boolean;
  view?: AreaView;
  /** Controlled selection (master/detail split). A table id or null. Omit for
   *  the legacy single pane. */
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const tablesQ = useQuery({
    queryKey: ["apps", appId, "tables"],
    queryFn: () => appsApi.listTables(appId),
  });
  const tables = React.useMemo(() => tablesQ.data ?? [], [tablesQ.data]);

  const controlled = onSelectId !== undefined;
  const [internalId, setInternalId] = React.useState<string | null>(null);
  const selectedId = controlled ? selectedIdProp ?? null : internalId;
  const setSelectedId = (id: string | null) => (controlled ? onSelectId!(id) : setInternalId(id));

  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");

  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter((x) => x.name.toLowerCase().includes(q));
  }, [tables, search]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["apps", appId, "tables"] });

  const createMut = useMutation({
    mutationFn: () => appsApi.createTable(appId, { name: nextTableName(tables) }),
    onSuccess: (saved) => {
      invalidate();
      setError(null);
      setSelectedId(saved.id);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => appsApi.removeTable(appId, id),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const selected = selectedId ? tables.find((t) => t.id === selectedId) ?? null : null;

  // --- MASTER (list) ---------------------------------------------------------
  const masterCls =
    view === "full"
      ? "flex w-56 shrink-0 flex-col border-r border-border/60 pr-2"
      : "flex h-full min-h-0 flex-col";

  const master = (
    <div className={masterCls}>
      <div className="mb-2 shrink-0 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Tabelle
      </div>
      {canWrite && (
        <button
          type="button"
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
          className="mb-1 flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Nuova tabella
        </button>
      )}
      {tables.length > 5 && (
        <input
          className="mb-1 rounded border border-border bg-background px-1.5 py-1 text-[11px]"
          placeholder="Cerca…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {tables.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">
            Nessuna tabella. Creane una con «Nuova».
          </p>
        ) : visible.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">Nessun risultato.</p>
        ) : (
          visible.map((tb) => (
            <button
              key={tb.id}
              type="button"
              onClick={() => setSelectedId(tb.id)}
              className={`mb-0.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] ${
                selectedId === tb.id ? "bg-accent/15 text-accent" : "hover:bg-surface"
              }`}
            >
              <Table2
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
                strokeWidth={1.75}
              />
              <span className="min-w-0 flex-1 truncate font-medium">{tb.name}</span>
              <span className="shrink-0 text-[9px] tabular-nums text-muted-foreground">
                {tb.columns_count}
              </span>
            </button>
          ))
        )}
      </div>
      {error && (
        <pre className="mt-1 shrink-0 whitespace-pre-wrap break-words rounded border border-danger/40 bg-danger/5 p-1.5 font-mono text-[10px] text-danger">
          {error}
        </pre>
      )}
    </div>
  );

  // --- DETAIL ----------------------------------------------------------------
  const detailInner = !selected ? (
    <div className="flex flex-1 items-center justify-center text-[12px] text-muted-foreground">
      Seleziona una tabella dalla lista, o creane una nuova.
    </div>
  ) : (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-2 flex shrink-0 items-center gap-2 border-b border-border/50 pb-2">
        <Table2 className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{selected.name}</span>
        {canWrite && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Eliminare la tabella "${selected.name}"? I suoi dati vanno persi.`))
                deleteMut.mutate(selected.id);
            }}
            className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-danger hover:bg-danger/10"
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Elimina
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <TableDesigner appId={appId} tableId={selected.id} canWrite={canWrite} />
      </div>
    </div>
  );

  if (view === "list") return master;
  if (view === "detail")
    return <div className="flex h-full min-h-0 flex-col text-[12px]">{detailInner}</div>;

  return (
    <div className="flex h-full min-h-0 gap-3 text-[12px]">
      {master}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{detailInner}</div>
    </div>
  );
}
